import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Breadcrumb, Button } from '@/lib/v2/components';

import {
  htmlEditorAtom,
  isLoadingCFAtom,
  isLoadingSaveAtom,
  isUploadFilePageModalOpenAtom,
} from '@/src/modules/CampaignsModule/atoms/htmlEditorAtom';

import { useSaveHtml } from './hooks';

import { AUTOMATION_PATHS } from '@/modules/AutomationModuleNew/constants';

interface HeaderSectionProps {
  isAutomationModule?: boolean;
  disabled?: boolean;
}

const HeaderSection = ({ isAutomationModule, disabled = false }: HeaderSectionProps) => {
  const { t } = useTranslation(['editorHtml']);
  const navigate = useNavigate();
  const setIsUploadFileOpen = useSetAtom(isUploadFilePageModalOpenAtom);
  const { exportEditorContent, handlePreview } = useSaveHtml();
  const setHtml = useSetAtom(htmlEditorAtom);
  const { pathname } = useLocation();
  const backToJourneyDetail = pathname.split('/').slice(0, -1).join('/');
  const isJourneyPath = pathname.includes(
    `${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.JOURNEYS_PATH}`
  );

  const isLoadingSave = useAtomValue(isLoadingSaveAtom);
  const [isLoadingCF, setIsLoadingCF] = useAtom(isLoadingCFAtom);

  const resetHTML = useCallback(() => {
    setIsLoadingCF(true);
    setHtml(RESET);
    isJourneyPath ? navigate(backToJourneyDetail) : navigate(-1);
  }, [setIsLoadingCF, setHtml, isJourneyPath, navigate, backToJourneyDetail]);

  const HeaderSectionButtons = useMemo(() => {
    return (
      <>
        <Button
          gray
          outline
          className="mx-1"
          disabled={disabled || isLoadingCF}
          id="header-html-upload__preview"
          size="small"
          onClick={() => void handlePreview(isAutomationModule)}
        >
          {t('preview')}
        </Button>
        <Button
          gray
          outline
          className="mx-1"
          disabled={disabled || isLoadingCF}
          id="header-html-upload__upload"
          size="small"
          onClick={() => setIsUploadFileOpen(true)}
        >
          {t('uploadHtml')}
        </Button>
        <Button
          className="mx-1"
          disabled={disabled || isLoadingCF}
          id="header-html-upload__save"
          isLoading={isLoadingSave}
          size="small"
          onClick={exportEditorContent}
        >
          {t('saveMessage')}
        </Button>
      </>
    );
  }, [
    exportEditorContent,
    handlePreview,
    isAutomationModule,
    isLoadingCF,
    isLoadingSave,
    setIsUploadFileOpen,
    t,
    disabled,
  ]);

  const HeaderSectionPaths = useMemo(
    () => [
      {
        id: 'header-html-path',
        name: t('messageEditor'),
      },
    ],
    [t]
  );

  return (
    <Breadcrumb
      actionButtons={HeaderSectionButtons}
      customPaths={HeaderSectionPaths}
      handleOnBackClick={resetHTML}
    />
  );
};

export default HeaderSection;
